import React, { useState, useEffect } from 'react';
import { Form, Input } from 'antd';
import styles from './styles.module.css';
import Header from '../../../components/header/Header';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFujiAuth } from '../../../auth/useFujiAuth';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import { isEmptyObject, showNotificationDialog } from '../../../utils';
import ButtonNetwork from '../../../utils/network-connect/ButtonNetwork';
import { EMAIL_REGEX_PATTERN, IS_IN_PURCHASE, localStorageKeys } from '../../../utils/Constants';

/**
 * 図101-2 メールアドレスログイン画面
 */
const EmailLogin = () => {
    useEffect(() => {}, []);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(false);

    const { signIn, resendConfirmationCode } = useFujiAuth();

    /**
     * ログインの処理
     *
     * @param {String} email
     * @param {String} password
     *
     */
    const onFinish = async ({ email, password }: { email: string; password: string }) => {
        setIsLoading(true);
        signIn(email, password, (result: any) => {
            if (result.isSuccessed) {
                // チケット情報がある場合は購入画面に遷移、ない場合はマイページに遷移
                showNotificationDialog(
                    'success',
                    'login_successfully',
                    t('notification.title'),
                    t('notification.login-success'),
                );

                // ログインに成功して、画面遷移を行う
                const isInPurchase = localStorage.getItem(IS_IN_PURCHASE) !== null;
                const loginFlow = JSON.parse(localStorage.getItem(localStorageKeys.LOGIN_FLOW) ?? '{}');

                if (isInPurchase || !isEmptyObject(loginFlow)) {
                    // 購入中もしくは購入済み一覧からログインした場合は戻る。
                    navigate(loginFlow.path, { state: loginFlow.state });
                } else {
                    navigate('/profile');
                }
            } else if (!result.isSuccessed && result.message.includes('User is not confirmed')) {
                // メールアドレスを確認していない場合
                resendConfirmationCode(
                    email,
                    ({ isSuccessed, error }: { isSuccessed: boolean; error: { code: string; message: string[] } }) => {
                        if (isSuccessed) {
                            // 確認コードページに遷移
                            navigate('/auth/confirm', { state: { email } });
                        } else if (error.code === 'LimitExceededException') {
                            // 1-E2 2-E4 確認コードのメールの送信上限に達している。
                            showNotificationDialog(
                                'error',
                                'resend-confirm-code',
                                t('notification.title'),
                                t('error-message.login.send-code-limit'),
                            );
                        } else {
                            // 1-E3 2-E5 確認コードのメール送信に失敗
                            showNotificationDialog(
                                'error',
                                'resend-confirm-code',
                                t('notification.title'),
                                t('error-message.login.send-code-failed') + ' ' + error.code,
                            );
                        }
                    },
                );
            } else {
                // ログインに失敗した場合
                if (
                    result.message.includes('Incorrect username or password') ||
                    result.message.includes('User does not exist')
                ) {
                    // 2-E2 パスワード不一致エラー
                    showNotificationDialog(
                        'error',
                        'login_failed',
                        t('notification.title'),
                        t('error-message.login.incorrect-password'),
                    );
                } else if (result.message.includes('Network error')) {
                    showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
                } else {
                    // 2-E3 その他のログインエラー
                    showNotificationDialog(
                        'error',
                        'login_failed',
                        t('notification.title'),
                        t('error-message.login.failed') + ' ' + result.message,
                    );
                }
            }
            setIsLoading(false);
        });
    };
    const onFinishFailed = () => {
        setIsLoading(false);
    };

    return (
        <React.Fragment>
            {isLoading && <CenterLoading overFooter={true} />}
            <Header title={t('header.login')} />
            <div className="content-footer-container scrollable">
                <div className={styles.loginFormContainer}>
                    <Form
                        className={styles.loginForm}
                        initialValues={{
                            remember: true,
                        }}
                        onFinish={(res) => void onFinish(res)}
                        onFinishFailed={onFinishFailed}
                    >
                        <Form.Item
                            name="email"
                            initialValue={location.state?.email}
                            rules={[
                                {
                                    pattern: EMAIL_REGEX_PATTERN,
                                    message: t('validate.correct-email') ?? '',
                                },
                                {
                                    required: true,
                                    message: t('validate.email') ?? '',
                                },
                            ]}
                        >
                            <Input
                                style={{ borderRadius: 4, height: 50 }}
                                placeholder={t('form.email') ?? ''}
                                size="large"
                                autoComplete=""
                                allowClear
                            />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: t('validate.password') ?? '',
                                },
                            ]}
                        >
                            <Input.Password
                                style={{ borderRadius: 4, height: 50 }}
                                placeholder={t('form.password') ?? ''}
                                size="large"
                                autoComplete=""
                            />
                        </Form.Item>
                        <Link to={'/auth/forgot-password'}>{t('login.forgot-password')}</Link>
                        <Form.Item>
                            <ButtonNetwork title={t('button.login')} type="submit" />
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default EmailLogin;
